import type React from 'react';
import { Fragment } from 'react';

import { startCase } from 'lodash-es';

import { cn } from '#src/app/shared/components/adminShadcn';

import type { AudienceBuilderAllowedFilter, AudienceBuilderFilter } from '../shared/types';

type FilterVariant = 'inclusion' | 'exclusion';

/** Read-only shape of audience filter groups, accepting both mutable form state and read-only GraphQL data. */
type FilterGroups = ReadonlyArray<readonly AudienceBuilderFilter[]>;

const SECTION_STYLES: Record<FilterVariant, { label: string; group: string; filter: string; or: string; and: string }> =
	{
		inclusion: {
			label: 'admin:text-teal-800',
			group: 'admin:bg-slate-100',
			filter: 'admin:text-teal-800',
			or: 'admin:border admin:border-green-300 admin:bg-white admin:text-teal-700',
			and: 'admin:bg-green-100 admin:text-teal-800',
		},
		exclusion: {
			label: 'admin:text-red-800',
			group: 'admin:bg-slate-100',
			filter: 'admin:text-red-800',
			or: 'admin:border admin:border-red-300 admin:bg-white admin:text-red-700',
			and: 'admin:bg-red-100 admin:text-red-800',
		},
	};

/** Turn the raw filter groups into non-empty groups of human-readable labels. */
function toLabelGroups(
	groups: FilterGroups | undefined,
	allowedFilters: readonly AudienceBuilderAllowedFilter[],
): string[][] {
	// Form state can briefly hold a non-array value (e.g. when the last filter of
	// a section is removed), so guard against anything that isn't an array.
	const groupList: FilterGroups = Array.isArray(groups) ? groups : [];
	return groupList
		.map((group) =>
			group
				.filter((filter) => filter.type || filter.value)
				.map((filter) => {
					const name = allowedFilters.find(({ type }) => type === filter.type)?.name ?? startCase(filter.type);
					return filter.value ? `${name}: ${filter.value}` : name;
				}),
		)
		.filter((group) => group.length > 0);
}

const FilterSection: React.FC<{ label: string; variant: FilterVariant; groups: string[][] }> = ({
	label,
	variant,
	groups,
}) => {
	const styles = SECTION_STYLES[variant];
	return (
		<div className="admin:flex admin:flex-col admin:gap-1.5 admin:text-sm">
			<span className={cn('admin:font-semibold', styles.label)}>{label}</span>
			<div className="admin:flex admin:flex-wrap admin:items-center admin:gap-2">
				{groups.map((group, groupIndex) => (
					// eslint-disable-next-line react/no-array-index-key
					<Fragment key={groupIndex}>
						{groupIndex > 0 && (
							<span
								className={cn('admin:rounded-full admin:px-2 admin:py-0.5 admin:text-xs admin:font-bold', styles.and)}
							>
								AND
							</span>
						)}
						<div
							className={cn(
								'admin:flex admin:flex-wrap admin:items-center admin:gap-1.5 admin:rounded-md admin:px-2 admin:py-1',
								styles.group,
							)}
						>
							{group.map((filterLabel, filterIndex) => (
								// eslint-disable-next-line react/no-array-index-key
								<Fragment key={filterIndex}>
									{filterIndex > 0 && (
										<span
											className={cn(
												'admin:rounded-full admin:px-2 admin:py-0.5 admin:text-xs admin:font-semibold',
												styles.or,
											)}
										>
											OR
										</span>
									)}
									<span className={cn('admin:text-xs', styles.filter)}>{filterLabel}</span>
								</Fragment>
							))}
						</div>
					</Fragment>
				))}
			</div>
		</div>
	);
};

/**
 * Read-only summary of an audience builder's filters, rendered as chips: each
 * AND clause is a colour-coded group box (lime-green inclusions / red exclusions)
 * with `OR` chips between alternatives, and `AND` chips between the groups. The
 * row wraps when long. Returns `null` when there are no filters.
 */
export const AudienceFiltersPreview: React.FC<{
	inclusions?: FilterGroups;
	exclusions?: FilterGroups;
	allowedFilters: readonly AudienceBuilderAllowedFilter[];
}> = ({ inclusions, exclusions, allowedFilters }) => {
	const inclusionGroups = toLabelGroups(inclusions, allowedFilters);
	const exclusionGroups = toLabelGroups(exclusions, allowedFilters);

	if (!inclusionGroups.length && !exclusionGroups.length) {
		return null;
	}

	return (
		<div className="admin:flex admin:flex-col admin:gap-2">
			{inclusionGroups.length > 0 && <FilterSection label="Inclusions:" variant="inclusion" groups={inclusionGroups} />}
			{exclusionGroups.length > 0 && <FilterSection label="Exclusions:" variant="exclusion" groups={exclusionGroups} />}
		</div>
	);
};
